import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection } from '../../components/footer/ContentSection';
import { TestimonialCard } from '../../components/footer/TestimonialCard';
import { CTASection } from '../../components/footer/CTASection';

export const PatientStoriesPage: React.FC = () => {
  const theme = useTheme();

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Physical Therapy Patient",
      location: "Austin, TX",
      rating: 5,
      text: "After my knee surgery, I was struggling with mobility. My physical therapist through Careva was amazing - patient, knowledgeable, and helped me get back to my active lifestyle. The convenience of in-home care made all the difference.",
      service: "Physical Therapy",
    },
    {
      name: "Robert Chen",
      role: "Senior Care Patient",
      location: "Seattle, WA",
      rating: 5,
      text: "The occupational therapist helped my father regain independence after his stroke. She was professional, caring, and worked with our family to create a comprehensive care plan. We're so grateful for the support.",
      service: "Occupational Therapy",
    },
    {
      name: "Maria Rodriguez",
      role: "Home Health Patient",
      location: "Miami, FL",
      rating: 5,
      text: "Finding quality home health care for my mother was challenging until we found Careva. The nurse was compassionate, skilled, and made my mother feel comfortable. The family support was incredible.",
      service: "Home Health Care",
    },
    {
      name: "David Thompson",
      role: "Physical Therapy Patient",
      location: "Denver, CO",
      rating: 5,
      text: "I was skeptical about virtual physical therapy, but my therapist made it work perfectly. The exercises were tailored to my needs, and I saw real progress. The flexibility of scheduling was a game-changer.",
      service: "Virtual Physical Therapy",
    },
    {
      name: "Jennifer Lee",
      role: "Occupational Therapy Patient",
      location: "Portland, OR",
      rating: 5,
      text: "After my car accident, I needed help with daily activities. My occupational therapist was patient and creative in finding solutions. She helped me adapt my home and regain confidence in my abilities.",
      service: "Occupational Therapy",
    },
    {
      name: "Michael Brown",
      role: "Senior Care Patient",
      location: "Phoenix, AZ",
      rating: 5,
      text: "The senior care specialist helped my wife with her dementia care. She was knowledgeable about the condition and provided both practical help and emotional support to our family during a difficult time.",
      service: "Senior Care",
    },
  ];

  const successStories = [
    {
      title: "From Wheelchair to Walking",
      patient: "Sarah Johnson",
      condition: "Post-Knee Surgery Recovery",
      duration: "3 months",
      outcome: "Full mobility restored, returned to hiking and cycling",
      story: "Sarah underwent knee surgery and was told she might need 6 months to recover. With dedicated physical therapy through Careva, she was back to her active lifestyle in just 3 months.",
    },
    {
      title: "Independence Regained",
      patient: "Robert Chen's Father",
      condition: "Post-Stroke Recovery",
      duration: "6 months",
      outcome: "Independent living restored, able to perform daily activities",
      story: "After a stroke, Robert's father struggled with basic daily tasks. Occupational therapy helped him regain independence and confidence in his abilities.",
    },
    {
      title: "Quality of Life Improved",
      patient: "Maria Rodriguez's Mother",
      condition: "Chronic Health Conditions",
      duration: "Ongoing",
      outcome: "Stable health, improved quality of life, family peace of mind",
      story: "Maria's mother needed ongoing home health care. The consistent, quality care provided through Careva improved her health and gave the family peace of mind.",
    },
  ];

  const statistics = [
    {
      value: "95%",
      label: "Patient Satisfaction",
      description: "Based on post-treatment surveys",
    },
    {
      value: "4.8/5",
      label: "Average Rating",
      description: "From patient reviews",
    },
    {
      value: "10,000+",
      label: "Patients Helped",
      description: "Since platform launch",
    },
    {
      value: "98%",
      label: "Would Recommend",
      description: "To friends and family",
    },
  ];

  return (
    <PageLayout
      title="Patient Stories & Success Stories"
      subtitle="Real stories from real patients who have found healing, hope, and improved quality of life through our platform."
      breadcrumbs={[
        { label: "For Patients", href: "/for-patients" },
        { label: "Patient Stories" },
      ]}
    >
      {/* Hero Section */}
      <ContentSection
        title="Stories of Healing and Hope"
        subtitle="Every patient has a unique journey. Read about the experiences of people who have found quality care and improved their lives through our platform."
        backgroundColor={theme.palette.grey[50]}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            These are real stories from real patients who have experienced the difference that quality, 
            personalized healthcare can make in their lives.
          </Typography>
        </Box>
      </ContentSection>

      {/* Statistics */}
      <ContentSection
        title="Patient Success by the Numbers"
        subtitle="Our patients consistently rate their experience highly and achieve positive outcomes."
      >
        <Grid container spacing={4}>
          {statistics.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    mb: 1,
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                >
                  {stat.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Patient Testimonials */}
      <ContentSection
        title="What Our Patients Say"
        subtitle="Read authentic reviews and testimonials from patients who have experienced our care."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <TestimonialCard {...testimonial} />
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Success Stories */}
      <ContentSection
        title="Success Stories"
        subtitle="Detailed stories of patient journeys and outcomes."
      >
        <Grid container spacing={4}>
          {successStories.map((story, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box
                sx={{
                  p: 3,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                >
                  {story.title}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Patient: {story.patient}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Condition: {story.condition}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Duration: {story.duration}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                    mb: 2,
                    flexGrow: 1,
                  }}
                >
                  {story.story}
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.success.light,
                    borderRadius: 1,
                    color: theme.palette.success.contrastText,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Outcome: {story.outcome}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Video Testimonials */}
      <ContentSection
        title="Video Testimonials"
        subtitle="Watch patients share their experiences in their own words."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: 2,
                textAlign: 'center',
                backgroundColor: 'white',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: 200,
                  backgroundColor: theme.palette.grey[200],
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                  Video Testimonial Coming Soon
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Sarah's Recovery Journey
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Watch Sarah share how physical therapy helped her recover from knee surgery.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: 2,
                textAlign: 'center',
                backgroundColor: 'white',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: 200,
                  backgroundColor: theme.palette.grey[200],
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                  Video Testimonial Coming Soon
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Robert's Family Story
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Hear how occupational therapy helped Robert's father regain independence.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ContentSection>

      {/* Share Your Story */}
      <ContentSection
        title="Share Your Story"
        subtitle="Have a story to share? We'd love to hear about your experience and how it might help others."
      >
        <Box sx={{ textAlign: 'center', maxWidth: '600px', mx: 'auto' }}>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            Your story could inspire others who are facing similar challenges. 
            Share your experience and help others find hope and healing.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="/contact"
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 'bold',
            }}
          >
            Share Your Story
          </Button>
        </Box>
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Ready to Start Your Success Story?"
        subtitle="Join thousands of patients who have found healing and hope through quality healthcare services."
        primaryButton={{
          text: "Find Your Caregiver",
          href: "/search",
        }}
        secondaryButton={{
          text: "Learn More",
          href: "/how-it-works",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
